// Maintenance-gate logic, extracted from MaintenanceGate.tsx during the
// Wave 2 refactor so each piece is independently testable.
//
//  - `isMaintenanceHost()` — pure host/query/localStorage check that
//    decides whether the gate should render.
//  - `captureAttribution()` — read-only snapshot of the current URL +
//    referrer + locale + viewport, capped at safe lengths.
//  - `WAITLIST_ENDPOINT`, `BYPASS_KEY`, `WAITLIST_LOCAL_KEY` — single
//    source of truth for the gate's external touch-points.
//  - `recordWaitlist()` — best-effort local capture so a flaky network
//    can't drop a lead. Returns whether the entry was newly added.
//
// Every function is window-safe (returns sane defaults under SSR) so
// the gate can be statically pre-rendered without crashing.

export const WAITLIST_ENDPOINT = 'https://formspree.io/f/mzdwavye';
// Pomela's own waitlist endpoint — the api-gateway worker double-writes
// each lead into Supabase using its service-role key. Formspree stays as
// the parallel "ops mailbox" pipe so launch-day reach-out has both a
// real DB to query and a dashboard view to scan.
//
// Phase 9: same-origin path. The Cloudflare Pages Function at
// functions/v1/[[path]].ts relays this to the api-gateway worker
// server-side, so the browser's Set-Cookie / CORS / ITP concerns are
// all gone. See functions/_lib/proxy.ts.
export const WAITLIST_PERSIST_ENDPOINT = '/v1/waitlist';
export const BYPASS_KEY = 'pomela:dev-bypass';
export const WAITLIST_LOCAL_KEY = 'pomela:waitlist';

const ATTR_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
] as const;

/**
 * Decide whether the maintenance gate should mount.
 *
 *  - `?gate=1` on any host forces ON (QA from localhost before deploy).
 *  - localhost / 127.0.0.1 / 0.0.0.0 / *.local → OFF (dev never sees it).
 *  - `?dev=1` or `pomela:dev-bypass=1` in localStorage → OFF (founder bypass).
 *  - pomela.app + *.pomela.app + *.pages.dev → ON (production hosts).
 *  - everything else → OFF.
 *
 * The implementation is `window`-safe — it returns `false` under SSR
 * so server-render frameworks never accidentally ship the gate.
 */
export function isMaintenanceHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  const search = window.location.search || '';

  if (/[?&]gate=1\b/.test(search)) return true;

  const isLocal =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.local');

  const bypass =
    /[?&]dev=1\b/.test(search) ||
    (() => {
      try {
        return localStorage.getItem(BYPASS_KEY) === '1';
      } catch {
        return false;
      }
    })();

  if (isLocal || bypass) return false;

  return (
    host === 'pomela.app' ||
    host === 'www.pomela.app' ||
    host.endsWith('.pomela.app') ||
    host.endsWith('.pages.dev')
  );
}

/**
 * Persist the founder bypass and reload. Used by the 5-tap wordmark
 * gesture. Returns false when localStorage is unavailable (Safari
 * private mode, locked-down iframes) — the caller can decide whether
 * to surface a toast or stay quiet (we stay quiet today).
 */
/**
 * §SIGNED-IN-IS-NOT-A-WAITLIST-CANDIDATE.
 *
 * MEASURED IN PRODUCTION 2026-09-02: a creator with an account, on the preview allowlist, holding a
 * valid session — /v1/auth/me answered signedIn:true through the same cookie jar — loaded pomela.app
 * and was shown "Get early access". The page made ZERO requests to the API on load, so it never asked
 * who the visitor was; the gate consulted query flags, localStorage and the hostname, and nothing
 * else.
 *
 * The waitlist exists for people WITHOUT access. Showing it to someone who already has access is not
 * a positioning choice, it is a wrong answer to a question the page never asked.
 *
 * The session cookie is HttpOnly, so the gate cannot read it synchronously. Instead the gate renders
 * immediately — no flash for the logged-out visitor, who is nearly everyone — and this asks the API
 * in the background. A signed-in creator is then let through to their workspace.
 *
 * Fails CLOSED: any error, timeout, or non-signed-in answer leaves the gate exactly as it was.
 */
export async function passIfSignedIn(apiBase: string): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  try {
    if (localStorage.getItem(BYPASS_KEY) === '1') return false; // already through
  } catch { /* storage blocked — carry on and ask anyway */ }
  try {
    const r = await fetch(`${apiBase}/v1/auth/me`, {
      credentials: 'include',
      signal: AbortSignal.timeout(6000),
    });
    if (!r.ok) return false;
    const j = (await r.json()) as { signedIn?: boolean };
    if (j?.signedIn !== true) return false;
    return activateBypass();
  } catch {
    return false;   // fail closed: the gate stays up
  }
}

export function activateBypass(): boolean {
  try {
    localStorage.setItem(BYPASS_KEY, '1');
    if (typeof window !== 'undefined') window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * The Phase 8 single source of truth for whether the maintenance gate
 * should render. Combines the build-time default
 * (`VITE_MAINTENANCE_MODE` → `defaultOn`) with the four runtime escape
 * hatches the founder relies on to enter the app while the gate stays
 * up for the public.
 *
 * Precedence (first match wins — do not reorder without updating tests):
 *
 *   1. `?gate=1`               → force ON
 *                                 QA preview hatch. Beats every bypass
 *                                 so the team can render the gate from
 *                                 localhost / a bypass'd browser
 *                                 without clearing state.
 *
 *   2. `?dev=1`                → force OFF
 *                                 Founder URL bypass. The most reliable
 *                                 path for first-time entry from an
 *                                 unprovisioned browser.
 *
 *   3. localStorage[BYPASS_KEY]='1'
 *                              → force OFF
 *                                 Persisted bypass set by the 5-tap
 *                                 wordmark gesture in MaintenanceGate.
 *                                 Survives reloads + sessions.
 *
 *   4. localhost / 127.0.0.1 / 0.0.0.0 / *.local
 *                              → force OFF
 *                                 Local dev never sees the gate. Phase 5
 *                                 dropped this; restoring it here keeps
 *                                 `pnpm dev` working without manual
 *                                 bypass setup.
 *
 *   5. (no match)              → defer to `defaultOn`
 *                                 The build-time env-var flag —
 *                                 typically `true` in production,
 *                                 `false` (or unset) elsewhere.
 *
 * IMPORTANT: this is a RUNTIME function. Vite cannot prove the App
 * branch is dead, so the full creator (CreatorRoot, AdminApp dynamic
 * import) stays in the bundle. The Phase 6 dead-code-elimination win
 * is the cost of this fix — the bypass affordances are worth more.
 */
export function shouldShowMaintenance(defaultOn: boolean): boolean {
  if (typeof window === 'undefined') return defaultOn;
  const search = window.location.search || '';

  // 1. ?gate=1 — force ON.
  if (/[?&]gate=1\b/.test(search)) return true;

  // 2. ?dev=1 — force OFF.
  if (/[?&]dev=1\b/.test(search)) return false;

  // 3. Persisted localStorage flag (set by 5-tap on the wordmark) —
  //    force OFF.
  try {
    if (localStorage.getItem(BYPASS_KEY) === '1') return false;
  } catch {
    /* localStorage blocked (Safari private mode, sealed iframe) —
       fall through to the next check */
  }

  // 4. Local dev hosts — force OFF.
  const host = window.location.hostname;
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.local')
  ) {
    return false;
  }

  // 5. Default — the build-time env-var flag.
  return defaultOn;
}

/**
 * Capture viral attribution at submit time. Best-effort: every read is
 * guarded so a hostile querystring or a sealed `document` never breaks
 * the form submission flow.
 *
 * Returns a flat string→string map (Formspree surfaces these as custom
 * fields in the dashboard) capped to safe lengths so we don't blow past
 * the form provider's per-field limits.
 */
export function captureAttribution(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === 'undefined') return out;
  try {
    const params = new URLSearchParams(window.location.search);

    const ref = params.get('ref');
    if (ref) out.ref = ref.slice(0, 120);

    for (const key of ATTR_KEYS) {
      const v = params.get(key);
      if (v) out[key] = v.slice(0, 120);
    }

    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    if (referrer && !referrer.startsWith(window.location.origin)) {
      try {
        const refURL = new URL(referrer);
        out.referrer_host = refURL.hostname;
        out.referrer = referrer.slice(0, 200);
      } catch {
        out.referrer = referrer.slice(0, 200);
      }
    }

    if (typeof navigator !== 'undefined' && navigator.language) {
      out.locale = navigator.language;
    }
    out.viewport = `${window.innerWidth}x${window.innerHeight}`;
  } catch {
    /* attribution capture is best-effort; never block submit */
  }
  return out;
}

/**
 * Local-first capture so a flaky network can't drop a lead. Idempotent —
 * a repeat submission of the same email is a no-op. Returns `true` when
 * the entry was newly added so the caller can fire telemetry once.
 */
export function recordWaitlist(email: string): boolean {
  try {
    const existing = JSON.parse(localStorage.getItem(WAITLIST_LOCAL_KEY) || '[]');
    if (!Array.isArray(existing)) return false;
    if (existing.includes(email)) return false;
    localStorage.setItem(WAITLIST_LOCAL_KEY, JSON.stringify([...existing, email]));
    return true;
  } catch {
    return false;
  }
}

/**
 * Stateful 5-tap detector. Each call returns whether the tap streak
 * has crossed the threshold (default 5 taps within 2s). The detector
 * is functional — the caller owns the array via `useState` so React
 * never sees a stale ref. The threshold + window are configurable so
 * future bypass affordances (e.g. a longer-hold mascot tap) can reuse it.
 */
export interface TapDetectorOptions {
  threshold?: number;
  windowMs?: number;
  now?: () => number;
}

export interface TapResult {
  taps: number[];
  triggered: boolean;
}

export function pushTap(
  prev: readonly number[],
  opts: TapDetectorOptions = {}
): TapResult {
  const threshold = opts.threshold ?? 5;
  const windowMs = opts.windowMs ?? 2000;
  const now = opts.now ?? Date.now;
  const t = now();
  const fresh = prev.filter((x) => t - x < windowMs);
  fresh.push(t);
  return { taps: fresh, triggered: fresh.length >= threshold };
}

/** Lightweight email validator — same regex the gate has shipped with. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
